import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { CONTINENTS, GENRES } from "@/lib/data";
import ArtistCard from "@/components/ArtistCard";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function Artists() {
  const [params] = useSearchParams();
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [genre, setGenre] = useState(params.get("genre") || "all");
  const [continent, setContinent] = useState(params.get("continent") || "all");

  const load = () => {
    setLoading(true);
    api
      .get("/artists", { params: { genre, continent, q: q || undefined } })
      .then((r) => setArtists(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [genre, continent]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14">
      <div className="text-center mb-12">
        <p className="text-[#C9A24B] text-xs tracking-[0.3em] uppercase mb-3">Le catalogue MYSE</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-white">Nos artistes d'exception</h1>
        <p className="text-[#A19E95] mt-4 max-w-2xl mx-auto">
          Explorez notre sélection privée d'artistes internationaux, classés par continent, style et genre musical.
        </p>
      </div>

      <div className="glass border border-[#C9A24B]/25 rounded-2xl p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A19E95]" />
          <Input
            data-testid="artists-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Rechercher un artiste..."
            className="bg-[#181820] border-white/10 text-white pl-9"
          />
        </div>
        <Select value={genre} onValueChange={setGenre}>
          <SelectTrigger data-testid="filter-genre" className="bg-[#181820] border-white/10 text-white">
            <SelectValue placeholder="Genre" />
          </SelectTrigger>
          <SelectContent className="bg-[#181820] border-white/10 text-white">
            <SelectItem value="all">Tous les genres</SelectItem>
            {GENRES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={continent} onValueChange={setContinent}>
          <SelectTrigger data-testid="filter-continent" className="bg-[#181820] border-white/10 text-white">
            <SelectValue placeholder="Continent" />
          </SelectTrigger>
          <SelectContent className="bg-[#181820] border-white/10 text-white">
            <SelectItem value="all">Tous les continents</SelectItem>
            {CONTINENTS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center text-[#A19E95] py-20">Chargement...</div>
      ) : artists.length === 0 ? (
        <div className="text-center text-[#A19E95] py-20" data-testid="artists-empty">
          Aucun artiste ne correspond à votre recherche.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="artists-grid">
          {artists.map((a, i) => <ArtistCard key={a.id} artist={a} index={i} />)}
        </div>
      )}
    </div>
  );
}
